import React, { useContext, useEffect } from "react";
import CommonModal from "../CommonModal";
import { Text } from "@src/components/Text";
import { ModalContext, ModalType } from "@src/components/modals/ModalController";
import styled from "@emotion/styled";
import { graphql, useStaticQuery } from "gatsby";
import { Flex } from "@src/components/Boxes";
import SignupForm, { SignupFormDataInput } from "../SignupForm";
import Translate from "../../translation/Translate";
import { logEvent as le } from "@src/utils/logClient";
import { Subheading, Space, theme } from "../../nessie-web";
import { useTeacherSignupMobileRedesign } from "@src/hooks/useTeacherSignupMobileRedesign";
import {
  LinkButton,
  LogoBadge,
  getTitleComponent,
  getMobileRedesignTextProps,
  getMobileRedesignModalProps,
} from "../shared/MobileRedesignComponents";

const {
  colors: { dt_taro10, dt_taro50 },
} = theme;

const SignToWatchWrapper = styled("div")`
  background-color: ${dt_taro10};
  padding: 16px;
  text-align: center;
`;

type TeacherSignupModalProps = {
  signToWatch?: string;
  closeModal: () => void;
  formData: SignupFormDataInput;
  fullPage?: boolean;
};

const TeacherSignupModal: React.FC<TeacherSignupModalProps> = (props) => {
  const data = useStaticQuery(graphql`
    {
      badge: file(name: { eq: "teacher_badge" }) {
        childImageSharp {
          gatsbyImageData(width: 88, quality: 90, placeholder: NONE, layout: FIXED)
        }
      }
    }
  `);

  const { badge } = data;

  const modalContext = useContext(ModalContext);

  useEffect(() => {
    le({ eventName: "externalPage.teacher_or_school_leader.signup_modal.shown", eventValue: "email_signup" });
  });

  const isTeacherSignupMobileRedesign = useTeacherSignupMobileRedesign();
  const ModalTitleComponent = getTitleComponent(isTeacherSignupMobileRedesign);
  const textProps = getMobileRedesignTextProps(isTeacherSignupMobileRedesign);

  function showLoginModal() {
    modalContext.showModal(ModalType.TeacherLogin);
  }

  return (
    <CommonModal noHeader {...getMobileRedesignModalProps(isTeacherSignupMobileRedesign)} {...props}>
      <LogoBadge isMobileOptimized={isTeacherSignupMobileRedesign} badge={badge} />
      <Flex flexDirection="column" alignItems="center" maxWidth={["100%", props.fullPage ? "545px" : "85%"]} mx="auto">
        <ModalTitleComponent as="h2">
          <Translate path="components.signup_teacher.ab_title_teacher" />
        </ModalTitleComponent>
        {props.signToWatch && (
          <>
            <SignToWatchWrapper>
              <Subheading color={dt_taro50}>{props.signToWatch}</Subheading>
            </SignToWatchWrapper>
            <Space size="m" />
          </>
        )}
        <div>
          <Text display="inline-block" {...textProps}>
            <Translate path="components.signup_teacher.subtitle" />
          </Text>
          <LinkButton
            onClick={() => {
              le({
                eventName: "web.teacher.user_signup.signup_form.login_instead.tap",
                eventValue: "domain_email_signup_overlay_variant",
              });
              showLoginModal();
            }}
          >
            <Translate path="components.signup_teacher.log_in_here" />
          </LinkButton>
        </div>
        <SignupForm formData={props.formData} userType="teacher" newStyles={isTeacherSignupMobileRedesign} />
      </Flex>
    </CommonModal>
  );
};

export default TeacherSignupModal;
